import { useState, useCallback, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import { useRef } from "react";
import PersonForm from "./components/PersonForm";
import MonthForm from "./components/MonthForm";
import WorkTable from "./components/WorkTable";
import "./App.css";
import CONSTS from "./shared/constants.json";

// ── DB stub: 将来SQLに差し替えるところ ────────────────────
// 実際の運用では fetch("/api/person/:id") に置き換える
const PERSON_DB = {
  "P001": {
    name: "明治太郎", furigana: "メイジ タロウ",
    dob: "2002-04-01", faculty: "総合数理学部", school: "明治大学 情報コミュニケーション学部 3年",
    phone: "090-1234-5678", zip: "101-0032", address: "千代田区神田司町2-1",
    checkId: "A12345",
    bankName: "三菱UFJ", branchName: "神田支店", branchNo: "001", branchFuri: "カンダ",
    accountType: "普通", accountNo: "1234567", accountName: "メイジ タロウ",
  },
};

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const getWeekday = (year, month, day) => {
  const d = new Date(year, month - 1, day);
  return ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
};

export default function App() {
  const [tab, setTab] = useState("month"); // "month" | "person" | "preview"
  const [status, setStatus] = useState(null);

  // ── 月次情報 ─────────────────────────────────────────────
  const [monthInfo, setMonthInfo] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    kenkyuShubetsu: "その他研究費",
    kenkyuShumoku: (CONSTS.KENKYU_Sonota && CONSTS.KENKYU_Sonota[0]) || "",
    daihyoushaKubun: "",
    daishaKbn: "",
    kanrishaShozoku: "",
    kanrishaName: "中村聡史",
    hourlyWage: 1250,
  });

  const initRows = useCallback((year, month) => {
    const n = getDaysInMonth(year, month);
    return Array.from({ length: n }, (_, i) => ({
      day: i + 1,
      weekday: getWeekday(year, month, i + 1),
      startTime: "",
      endTime: "",
      breakTime: "",
      workHours: "",
      lateHours: "",
      totalHours: "",
      content: "",
      active: false,
    }));
  }, []);

  // ── 従事者情報（将来DB連携） ──────────────────────────────
  const [personId, setPersonId] = useState(() => {
    return localStorage.getItem('meiji_personId') || 'P001';
  });
  const [person, setPerson] = useState(() => {
    try { return JSON.parse(localStorage.getItem('meiji_person')) || PERSON_DB['P001']; } catch (e) { return PERSON_DB['P001']; }
  });
  const [rows, setRows] = useState(() => {
    try { return JSON.parse(localStorage.getItem('meiji_rows')) || initRows(monthInfo.year, monthInfo.month); } catch (e) { return initRows(monthInfo.year, monthInfo.month); }
  });
  // ── プレビュー用 state ─────────────────────────────────────
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const previewIFrameRef = useRef(null);
  const previewPdfIframeRef = useRef(null);

  // persist personId/person/rows to localStorage
  useEffect(() => { localStorage.setItem('meiji_personId', personId); }, [personId]);
  useEffect(() => { localStorage.setItem('meiji_person', JSON.stringify(person)); }, [person]);
  useEffect(() => { localStorage.setItem('meiji_rows', JSON.stringify(rows)); }, [rows]);

  const loadPerson = useCallback((id) => {
    const p = PERSON_DB[id];
    if (p) { setPerson(p); setPersonId(id); }
    if (p) {
      setMonthInfo(prev => ({ ...prev, kanrishaShozoku: p.faculty || prev.kanrishaShozoku }));
    }
  }, []);

  // ── 勤務行 ───────────────────────────────────────────────
  const numDays = useMemo(
    () => getDaysInMonth(monthInfo.year, monthInfo.month),
    [monthInfo.year, monthInfo.month]
  );

  // const [rows, setRows] = useState(() => initRows(monthInfo.year, monthInfo.month));

  const handleMonthChange = useCallback((field, value) => {
    setMonthInfo(prev => {
      const next = { ...prev, [field]: value };
      if (field === "year" || field === "month") {
        setRows(initRows(next.year, next.month));
      }
      return next;
    });
  }, [initRows]);

  const updateRow = useCallback((idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // 従事時間数の自動計算
      if (field === "startTime" || field === "endTime" || field === "breakTime") {
        const r = next[idx];
        if (r.startTime && r.endTime) {
          const toMin = (t) => {
            const [h, m] = t.split(":").map(Number);
            return h * 60 + (m || 0);
          };
          const breakMin = r.breakTime ? toMin(r.breakTime) : 0;
          const totalMin = toMin(r.endTime) - toMin(r.startTime) - breakMin;
          if (totalMin > 0) {
            const lateStart = Math.max(toMin(r.startTime), 22 * 60);
            const lateMin = Math.max(0, toMin(r.endTime) - lateStart);
            const workMin = totalMin - lateMin;
            const fmt = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
            next[idx].workHours = fmt(workMin);
            next[idx].lateHours = fmt(lateMin);
            next[idx].totalHours = fmt(workMin + lateMin);
          }
        }
      }
      return next;
    });
  }, []);

  const toggleRow = useCallback((idx) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], active: !next[idx].active };
      if (!next[idx].active) {
        next[idx] = { ...next[idx], startTime: "", endTime: "", breakTime: "", workHours: "", lateHours: "", totalHours: "", content: "" };
      }
      return next;
    });
  }, []);

  // ── Excel出力 ────────────────────────────────────────────
  const handleExport = async () => {
    setStatus("loading");
    try {
      const activeRows = rows.filter(r => r.active);
      // monthInfo に管理者所属が無ければ person.faculty をフォールバック
      const monthToSend = { ...monthInfo, kanrishaShozoku: monthInfo?.kanrishaShozoku || person.faculty || "" };
      const res = await fetch("/api/excel/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          person,
          month: monthToSend,
          rows: activeRows,
          hourlyWage: monthInfo.hourlyWage,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `勤務実績報告書_${monthInfo.year}年${monthInfo.month}月_${person.name}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus("done");
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setTimeout(() => setStatus(null), 4000);
    }
  };

  // ── プレビュー ────────────────────────────────────────────
  const handlePreview = async () => {
    try {
      const activeRows = rows.filter(r => r.active);
      const monthToSend = { ...monthInfo, kanrishaShozoku: monthInfo?.kanrishaShozoku || person.faculty || "" };
      const res = await fetch("/api/excel/publish-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ person, month: monthToSend, rows: activeRows, hourlyWage: monthInfo.hourlyWage }),
      });
      if (!res.ok) throw new Error(await res.text());
      // const { url } = await res.json();

      // // 1) そのまま新タブで開く（ブラウザが処理できれば）
      // // window.open(url, "_blank");

      // const backendHost = window.location.hostname || "localhost";
      // const backendPort = 3001;
      // const url2 = `http://${backendHost}:${backendPort}/public/${encodeURIComponent(fname)}`;
      // console.log("preview url ->", url2);
      // window.open(url2, "_blank");
      const { fname } = await res.json();
      // Apache が /public をバックエンドへ中継するため、同一オリジンのURLを使う。
      // これにより本番HTTPSでも mixed content にならない。
      const previewUrl = `/public/${encodeURIComponent(fname)}`;
      console.log("preview url ->", previewUrl);
      window.open(previewUrl, "_blank");

      // 2) Office Online で開く（公開 https URL が必要。ローカルは ngrok 等で公開してから）
      // const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      // window.open(officeUrl, "_blank");
    } catch (e) {
      console.error(e);
      alert("プレビューに失敗しました: " + (e.message || e));
    }
  };

  // 印刷ボタン（簡易実装：現在のページをブラウザの印刷ダイアログで印刷）
  const handlePrint = () => {
    window.print();
  };

  const totalWorkMin = rows.reduce((sum, r) => {
    if (!r.workHours) return sum;
    const [h, m] = r.workHours.split(":").map(Number);
    return sum + h * 60 + (m || 0);
  }, 0);
  const totalH = Math.floor(totalWorkMin / 60);
  const totalM = totalWorkMin % 60;
  const estimatedPay = Math.ceil(totalWorkMin / 60 * monthInfo.hourlyWage);

  return (
    <div className="app-shell">
      {/* プレビュー・モーダル */}
      {previewHtml && (
        <div className="preview-modal" style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200
        }}>
          <div style={{ width: "92%", height: "88%", background: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.3)", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: 8, display: "flex", gap: 8, justifyContent: "flex-end", borderBottom: "1px solid #eee" }}>
              <button className="btn" onClick={() => previewIFrameRef.current?.contentWindow?.print()}>印刷</button>
              <button className="btn" onClick={() => { setPreviewHtml(null); }}>閉じる</button>
            </div>
            <iframe
              ref={previewIFrameRef}
              title="勤務実績プレビュー"
              srcDoc={previewHtml}
              style={{ border: 0, width: "100%", height: "100%" }}
            />
          </div>
        </div>
      )}
      {/* ── Toolbar ── */}
      <header className="toolbar">
        <div className="toolbar-left">
          <span className="app-icon">📋</span>
          <div>
            <h1 className="app-title">勤務実績報告書 入力システム</h1>
            <p className="app-subtitle">明治大学 研究補助業務（アルバイト）申請書</p>
          </div>
        </div>
        <div className="toolbar-right">
          {status === "done" && <span className="badge success">✓ Excel出力しました</span>}
          {status === "error" && <span className="badge error">⚠ エラーが発生しました</span>}
          {status === "loading" && <span className="badge loading">生成中...</span>}
          <button className="btn btn-primary" onClick={handleExport} disabled={status === "loading"}>
            ↓ 勤務実績報告書を出力
          </button>
          <button className="btn" onClick={handlePreview} style={{ marginLeft: 8 }}>
            🔍 プレビュー
          </button>
          {/* <button className="btn" onClick={handlePrint} style={{ marginLeft: 8 }}>
            🖨 印刷する
          </button> */}
        </div>
      </header>

      {/* ── タブ ── */}
      <div className="tab-bar">
        {[["month", "① 月次・研究情報"], ["person", "② 従事者情報"], ["work", "③ 勤務実績入力"]].map(([key, label]) => (
          <button key={key} className={`tab-btn${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      <main className="main-content">
        {tab === "month" && <MonthForm info={monthInfo} onChange={handleMonthChange} />}
        {tab === "person" && <PersonForm person={person} onChange={(f, v) => setPerson(p => ({ ...p, [f]: v }))} personId={personId} onLoad={loadPerson} />}
        {tab === "work" && (
          <WorkTable
            rows={rows}
            year={monthInfo.year}
            month={monthInfo.month}
            onToggle={toggleRow}
            onUpdate={updateRow}
            totalH={totalH}
            totalM={totalM}
            estimatedPay={estimatedPay}
            hourlyWage={monthInfo.hourlyWage}
          />
        )}
      </main>
    </div>
  );
}
